import { ImportModal, InfoBar } from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBoardProvider } from "../../contexts/board/boardContext";
import importFileService from "../../data/api/importFileService";
import { useImportTasks } from "../../data/mutations/importFileMutations";
import { useImportBatch } from "../../data/queries/importFileQueries";
import useEvent from "../../hooks/useEvent";
import useModalEvent from "../../hooks/useModalEvent";

export const ImportTasksModal = () => {
  const { t, i18n } = useTranslation(["ImportTasksModal"]);
  const importModalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const { Events, toggleEvent } = useEvent();
  const [batchId, setBatchId] = useState(null);
  const { currentBoardId } = useBoardProvider();

  var queryClient = useQueryClient();

  const { getTasksExcelTemplate } = importFileService();
  const { mutate: importFile } = useImportTasks();
  const { data: importBatch, refetch: refetchImportBatch } = useImportBatch(
    batchId,
    {
      enabled: !!batchId // only fetch when batchId is available
    }
  );

  const downloadTemplate = async () => {
    const response = await getTasksExcelTemplate();
    const blob = new Blob([response.data]);
    saveAs(blob, response.filename);
  };

  useRegisterEvent(ModalEvents.ImportTasks, (e) => {
    if (e?.detail?.step) {
      if (e?.detail?.step == 2) {
        setTimeout(() => {
          importModalRef?.current
            ?.getStepperRef()
            .getButtonsRef()
            .setContinueText(t("ReturnToUpload"));
          importModalRef?.current
            ?.getStepperRef()
            .getButtonsRef()
            .setBackText(t("Cancel"));
        });
      }
      importModalRef?.current?.getStepperRef()?.goToStep(e?.detail?.step);
      setTimeout(() => {
        importModalRef?.current.setErrorData(e?.detail?.errors);
      });
    }

    importModalRef.current.open();
  });

  const refreshImportStatus = () => {
    const currentStep = importModalRef?.current?.getStepperRef()?.activeStep;

    if (batchId && currentStep === 1) {
      refetchImportBatch();

      if (importBatch?.Status === 2) {
        importModalRef?.current?.getStepperRef()?.goToStep(2);

        setTimeout(() => {
          setTimeout(() => {
            importModalRef?.current?.getStepperRef()?.setShowButtons(true);

            setTimeout(() => {
              importModalRef?.current
                ?.getStepperRef()
                ?.getButtonsRef()
                ?.setContinueText(t("ReturnToUpload"));

              importModalRef?.current
                ?.getStepperRef()
                ?.getButtonsRef()
                ?.setBackText(t("Cancel"));
            });
          });

          importModalRef?.current?.setErrorData(importBatch?.Errors);
        });

        setBatchId(null);
      } else if (importBatch?.Status === 3) {
        importModalRef?.current?.getStepperRef()?.goToStep(3);

        setBatchId(null);
        queryClient.invalidateQueries(["get-tags"]);
        queryClient.invalidateQueries(["get-board-permissions"]);
        toggleEvent(Events.RefreshTaskGrid);
      }

      toggleEvent(Events.RefreshImportLogOverlay);
    }
  };

  useEffect(() => {
    const buttonsRef = importModalRef.current.getStepperRef().getButtonsRef();
    buttonsRef.setCreateText(t("Create"));
    buttonsRef.setBackText(t("Cancel"));
    buttonsRef.setContinueText(t("Continue"));
  }, [i18n.language]);

  const header = (
    <div className="space-y-4">
      <p className="text-center fw-bold">{t("Title")}</p>
      <p className="text-center">{t("Instructions")}</p>
      <div className="d-flex justify-content-center">
        <a
          href="#"
          className="text-center"
          onClick={downloadTemplate}
          id={ModalEvents.ImportTasks.Id + "_download-template-link"}
        >
          {t("DownloadTemplate")}
        </a>
      </div>
    </div>
  );

  const maxRows = <InfoBar variant="information" message={t("MaxRows")} />;

  const handleUploadFiles = (file) =>
    new Promise((resolve, reject) => {
      importFile(
        { boardId: currentBoardId, file },
        {
          onSuccess: (response) => {
            if (response.Success) {
              setBatchId(response.Id);
              toggleEvent(Events.RefreshImportLogOverlay);
              resolve(response);
            } else {
              setTimeout(() => {
                importModalRef?.current?.getUploaderErrorRef().setError(null);
                importModalRef?.current?.getStepperRef()?.setShowButtons(true);
                importModalRef?.current?.setErrorMessage(response.Message);
              });
              reject(response);
            }
          },
          onError: (err) => {
            console.error(err);
            reject(err);
          }
        }
      );
    });

  return (
    <ImportModal
      header={header}
      uploaderHeader={maxRows}
      ref={importModalRef}
      id={ModalEvents.ImportTasks.Id}
      importTitle={t("Title")}
      lang={i18n.language}
      importMultiple={false}
      continueText={t("Continue")}
      backText={t("Cancel")}
      createText={t("Create")}
      uploadFiles={handleUploadFiles}
      onRefreshStatus={refreshImportStatus}
    />
  );
};
